<template>
	<div class="selection:bg-mx-orange selection:text-white">
		<!-- Default Label -->
		<label
			v-if="!floatingLabel && !hideLabel"
			:for="id"
		>
			<slot name="label">
				<FormLabel
					v-if="label?.length"
					:class="[
						labelSize,
						labelClass,
						'min-h-5'
					]"
				>
					{{ label }}
					<span
						v-if="required && showRequiredAstrisk"
						:class="[
							labelSize,
							'text-red-500 lowercase'
						]"
					>
						*
					</span>
				</FormLabel>
			</slot>
		</label>
		<div class="relative">
			<slot name="input">
				<!-- Floating Label | Standard Text Inputs -->
				<div
					v-if="floatingLabel"
					class="relative"
				>
					<input
						v-if="isStandardTextInput"
						:id="id"
						v-model="inputValue"
						class="w-full text-black placeholder-transparent outline-none appearance-none dark:text-mx-gray-400 placeholder:select-none peer autofill:bg-mx-orange focus:ring-0 focus:outline-none focus:border-t-transparent focus:border-x-transparent border-t-transparent border-x-transparent"
						:type="dynamicInputType"
						:class="[
							inputClass,
							borderValidationClass,
							borderClasses,
							{ 'rounded-lg': rounded },
							{ 'bg-mx-gray-300': disabled },
							{ 'pl-8': displayIcon && iconPosition === LEFT },
							`focus:ring-${ringColor}`,
							{ hidden: hideInput },
							backgroundColor,
							showErrorMessage ? 'invalid:border-b-red-500' : 'focus:border-b-mx-orange'
						]"
						:autocomplete="autocomplete"
						:maxlength="maxLength"
						:placeholder="placeholder"
						:disabled="disabled"
						:required="required"
						:readonly="readOnly"
						@blur="handleBlur"
						@focus="handleFocus"
						@keyup="handleKeyup"
					>

					<!-- Floating Label | Telephone Input -->
					<input
						v-if="inputType === TEL"
						:id="id"
						v-model="inputValue"
						:type="TEL"
						class="w-full text-black placeholder-transparent outline-none appearance-none dark:text-mx-gray-400 placeholder:select-none peer autofill:bg-mx-orange focus:ring-0 focus:outline-none focus:border-t-transparent focus:border-x-transparent border-t-transparent border-x-transparent dark:border-x-transparent dark:border-t-transparent"
						:class="[
							inputClass,
							borderValidationClass,
							borderClasses,
							{ 'rounded-lg': rounded },
							{ 'bg-mx-gray-300': disabled },
							{ 'pl-8': displayIcon && iconPosition === LEFT },
							`focus:ring-${ringColor} dark:[color-scheme:dark]`,
							{ hidden: hideInput },
							backgroundColor,
							showErrorMessage ? 'invalid:border-b-red-500' : 'focus:border-b-mx-orange'
						]"
						:autocomplete="autocomplete"
						:maxlength="maxLength"
						:placeholder="placeholder"
						:disabled="disabled"
						:required="required"
						:readonly="readOnly"
						@blur="handleBlur"
						@input="updateValue"
						@focus="handleFocus"
						@keyup="handleKeyup"
					>

					<!-- Floating Label | Error Message -->
					<div
						v-if="showErrorMessageOnInvalid"
						class="h-[20px]"
					>
						<Transition name="fade-and-slide">
							<div
								v-if="showErrorMessage"
								class="pt-1 pl-1 text-xs italic text-mx-dark-orange dark:text-mx-yellow"
							>
								{{ computedErrorMessage }}
							</div>
						</Transition>
					</div>

					<!-- Floating Label -->
					<label
						:for="id"
						:class="[
							'absolute pointer-events-none capitalize left-0 focus-within:left-0 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-mx-gray-500 peer-placeholder-shown:top-1 peer-focus:left-0 peer-focus:text-mx-gray-500 peer-focus:text-sm',
							{ 'peer-placeholder-shown:left-2': rounded },
							{ '-top-5': hasInputValue},
							readOnly ? '-mt-5 top-0' : 'peer-focus:-top-5'
						]"
					>
						<span
							:class="[
								labelSize,
								labelClass
							]"
						>
							{{ label }}
						</span>
						<span
							v-if="required && showRequiredAstrisk && !readOnly"
							:class="[
								labelSize,
								'text-red-500 lowercase'
							]"
						>
							*
						</span>
					</label>
				</div>

				<!-- Default Label | Standard Text Input -->
				<input
					v-if="isStandardTextInput && !floatingLabel"
					:id="id"
					v-model="inputValue"
					:type="dynamicInputType"
					class="w-full px-3 text-black rounded-lg h-11 placeholder-mx-gray-500 dark:text-mx-gray-400 focus:outline-none focus:ring-2"
					:class="[
						inputClass,
						borderValidationClass,
						borderClasses,
						{ 'bg-mx-gray-300': disabled },
						{ 'pl-8': displayIcon && iconPosition == LEFT },
						`focus:ring-${ringColor} dark:[color-scheme:dark]`,
						{ hidden: hideInput },
						backgroundColor
					]"
					:autocomplete="autocomplete"
					:placeholder="placeholder"
					:maxlength="maxLength"
					:disabled="disabled"
					:required="required"
					:readonly="readOnly"
					:min="min"
					@blur="handleBlur"
					@focus="handleFocus"
					@keyup="handleKeyup"
				>

				<!-- Default Label | Telephone Input -->
				<input
					v-if="!floatingLabel && inputType === TEL"
					:id="id"
					v-model="inputValue"
					type="tel"
					class="w-full px-3 rounded-lg placeholder-mx-gray-500 h-11 focus:outline-none focus:ring-2 dark:text-mx-gray-400"
					:class="[
						inputClass,
						borderValidationClass,
						borderClasses,
						{ 'bg-mx-gray-300': disabled },
						{ 'pl-8': displayIcon && iconPosition === LEFT },
						`focus:ring-${ringColor}`,
						{ hidden: hideInput },
						backgroundColor
					]"
					:autocomplete="autocomplete"
					:placeholder="placeholder"
					:maxlength="maxLength"
					:disabled="disabled"
					:required="required"
					:readonly="readOnly"
					@input="updateValue"
					@blur="handleBlur"
					@focus="handleFocus"
				>

				<!-- Default Label | Textarea Input -->
				<textarea
					v-if="inputType === TEXTAREA"
					:id="id"
					v-model="inputValue"
					:rows="rows"
					:cols="cols"
					:aria-label="t('aria.message')"
					class="px-3 rounded-lg placeholder-mx-gray-500 border-mx-gray-300 caret-mx-orange min-h-11 focus:outline-none focus:ring-2 focus:border-mx-gray-300 dark:text-mx-gray-400"
					:class="[
						inputClass,
						borderValidationClass,
						borderClasses,
						{ 'bg-mx-gray-300': disabled },
						{ 'pl-8': displayIcon && iconPosition === LEFT },
						`focus:ring-${ringColor}`,
						backgroundColor
					]"
					:placeholder="placeholder"
					:maxlength="maxLength"
					:disabled="disabled"
					:required="required"
					:readonly="readOnly"
					@blur="handleBlur"
				/>
				<!-- Icon -->
				<template v-if="displayIcon">
					<FormTextInputIcon :position="iconPosition">
						<slot name="icon">
							<IconSearch v-if="inputType === SEARCH" />
						</slot>
					</FormTextInputIcon>
				</template>
			</slot>
			<FormShowPasswordButton
				v-if="inputType === PASSWORD"
				v-model="showPassword"
				class="absolute right-3 top-2"
			/>
			<slot name="leading" />
			<slot name="trailing" />
		</div>
		<!-- Default Label | Error Message -->
		<div
			v-if="showErrorMessageOnInvalid"
			class="h-[20px]"
		>
			<Transition name="fade-and-slide">
				<slot name="accordion">
					<div
						v-if="showErrorMessage && !floatingLabel"
						class="pt-1 pl-1 text-xs italic text-mx-dark-orange dark:text-mx-yellow"
					>
						{{ computedErrorMessage }}
					</div>
				</slot>
			</Transition>
		</div>
		<div
			v-if="showInputInstruction"
			:class="[
				'py-1',
				{ '-mt-4': !floatingLabel }
			]"
		>
			<slot name="instruction">
				<div
					:class="[
						'pl-1 text-xs',
						isError ? 'text-mx-dark-orange dark:text-mx-yellow italic' : 'text-mx-gray-500 dark:text-mx-gray-400'
					]"
					v-html="inputInstruction"
				/>
			</slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	ref,
	toRefs,
	watch
} from 'vue'
import { useI18n } from 'vue-i18n'

import { inputStateValidator, inputTypeValidator } from '@/validators/text-inputs.js'

import type { CountryCode } from '@/components/form/types'
import { ERROR, NONE } from '@/constants/form/states.js'
import {
	DATE,
	EMAIL,
	NUMBER,
	PASSWORD,
	SEARCH,
	TEL,
	TEXT,
	TEXTAREA,
	TIME
} from '@/constants/form/text-inputs/types.js'
import type { InputRestriction } from '@/constants/form/text-inputs/validation/types'
import {
	EXPIRATION, SECURITY_CODE,
	USD,
	ZIP
} from '@/constants/form/text-inputs/validation/types'

type AsYouTypeType = typeof import('libphonenumber-js').AsYouType // TypeScript's type imports are guaranteed to be removed at runtime, so this should not affect bundle size.

let AsYouType: AsYouTypeType | undefined

const LEFT = 'left'
const RIGHT = 'right'
const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
	state: {
		type: [ String, Boolean ],
		default: NONE,
		validator: inputStateValidator
	},
	inputRestriction: {
		type: String as PropType<InputRestriction>,
		default: 'none'
	},
	label: {
		type: String,
		default: ''
	},
	maxLength: {
		type: Number,
		default: 250
	},
	floatingLabel: {
		type: Boolean,
		default: false
	},
	rounded: {
		type: Boolean,
		default: false
	},
	id: {
		type: String,
		required: true
	},
	placeholder: {
		type: String,
		required: true
	},
	disabled: {
		type: Boolean,
		default: false
	},
	required: {
		type: Boolean,
		default: false
	},
	inputClass: {
		type: String,
		default: ''
	},
	borderClass: {
		type: String,
		default: 'border-2 border-mx-gray-300 focus:border-mx-gray-300 dark:border-mx-gray-500 dark:focus:border-mx-gray-500 dark:border-x-transparent dark:border-t-transparent dark:focus:border-t-transparent dark:focus:border-x-transparent'
	},
	inputType: {
		type: String,
		default: TEXT,
		validator: inputTypeValidator
	},
	labelClass: {
		type: String,
		default: 'text-mx-gray-500 dark:text-mx-gray-300'
	},
	labelSize: { // any tailwind text size class
		type: String,
		default: 'text-sm'
	},
	ringColor: { // any tailwind color
		type: String,
		default: 'mx-orange'
	},
	iconAlt: {
		type: String,
		default: ''
	},
	showIcon: {
		type: Boolean,
		default: false
	},
	iconLeft: {
		type: Boolean,
		default: true
	},
	iconRight: {
		type: Boolean,
		default: false
	},
	autocomplete: {
		type: String,
		default: 'on'
	},
	countryCode: {
		type: String as PropType <CountryCode>,
		default: 'US'
	},
	showErrorMessageOnInvalid: {
		type: Boolean,
		default: true
	},
	errorMessage: {
		type: String,
		default: ''
	},
	showRequiredAstrisk: {
		type: Boolean,
		default: true
	},
	bgColor: {
		type: String,
		default: 'bg-white dark:bg-mx-green-700'
	},
	hideLabel: {
		type: Boolean,
		default: false
	},
	rows: {
		type: String,
		default: '5'
	},
	cols: {
		type: String,
		default: '0'
	},
	inputInstruction: {
		type: String,
		default: ''
	},
	readOnly: {
		type: Boolean,
		default: false
	},
	hideInput: {
		type: Boolean,
		default: false
	},
	shadowBox: {
		type: Boolean,
		default: false
	},
	lightBox: {
		type: Boolean,
		default: false
	},
	min: {
		type: [ Number, String ],
		default: 0
	}
})
const {
	required, inputType, iconLeft, iconRight, countryCode, showIcon, showErrorMessageOnInvalid, errorMessage, floatingLabel, label, id, showRequiredAstrisk, labelClass, labelSize, ringColor, hideLabel, disabled, placeholder, bgColor, state, inputInstruction, inputRestriction, readOnly, shadowBox, borderClass
} = toRefs(props)

const inputValue = defineModel<string | number | null>({
	default: ''
})

const emit = defineEmits([ 'updated', 'input', 'validity-changed', 'blur', 'focus', 'keyup' ])

const updateValue = (event: Event) => {
	if (isTelephoneInput.value) {
		validateAsYouType((event.target as HTMLInputElement).value)
	}
}
onMounted(() => {
	if (readOnly.value) {
		const element = document.getElementById(id.value)
		element?.setAttribute('readonly', 'true')
	}
})

const display = ref<string | null>(null)
const isValid = ref(false)
const enableErrorMessage = ref(false)
const showPassword = ref(false)

const inputRestrictionRegex = {
	'numeric': /\D/g,
	'alphanumeric': /[^a-zA-Z0-9]/g,
	'alphabetic': /[^a-zA-Z]/g,
	'expiration': /[^0-9/]/g,
	'security-code': /[^0-9]/g,
	'zip': /[^0-9]/g,
	'usd': /[^0-9]/g,
	'none': ''
}
watch(inputValue, newValue => {
	if (!newValue) {
		return
	}
	const regex = inputRestrictionRegex[inputRestriction.value]
	switch (inputRestriction.value) {
		case EXPIRATION:
			inputValue.value = newValue?.toString()?.replace(regex, '')?.substring(0, 5)
			break
		case ZIP:
			inputValue.value = newValue?.toString()?.replace(regex, '')?.substring(0, 5)
			break
		case SECURITY_CODE:
			inputValue.value = newValue?.toString()?.replace(regex, '')?.substring(0, 4)
			break
		case USD:
			if (inputValue.value?.toString()?.includes('.')) {
				const [ dollars, cents ] = newValue?.toString()?.split('.')
				inputValue.value = `${dollars.replace(regex, '')}.${cents?.replace(regex, '').substring(0, 2)}`
			} else {
				inputValue.value = newValue?.toString()?.replace(regex, '')
			}
			break
		default:
			inputValue.value = newValue?.toString()?.replace(regex, '')
	}
})

const hasInputValue = computed(() => {
	if (typeof inputValue.value === 'number') {
		return inputValue.value?.toString().length
	}
	return !!inputValue.value?.length
})

const dynamicInputType = computed(() => {
	if (inputType.value === PASSWORD && showPassword.value) {
		return TEXT
	}
	return inputType.value
})

const validateAsYouType = async (newValue: string) => {
	if (inputType.value !== TEL) {
		return
	} // stop if not a telephone input
	if (!AsYouType) {
		const lib = await import('libphonenumber-js')
		AsYouType = lib.AsYouType
	}
	const asYouType = new AsYouType(countryCode.value)
	if (!newValue?.length) {
		return
	}
	if (newValue?.length === 1) {
		return newValue
	}
	if (newValue?.length < 3) {
		display.value = newValue
		return
	}
	display.value = asYouType.input(newValue)
	let digits = asYouType.getNationalNumber()
	// trim country code if it exists in the digits
	if (digits?.length > 10) {
		digits = digits.slice(-10)
	}
	isValid.value = asYouType.isValid()
	emit('input', digits)
	emit('updated', asYouType)
	emit('validity-changed', isValid.value)
}

watch([ inputValue, display ], () => {
	if (!inputValue.value) {
		return
	}

	validateAsYouType(inputValue.value?.toString())

	if (inputType.value === TEL) {
		if (inputValue.value?.toString()?.length < 3) {
			return
		}
		if (display.value) {
			inputValue.value = display.value
		}
	}
}, { immediate: true })

const borderValidationClass = computed(() => {
	if (!hasInputValue.value) {
		return ''
	}
	return { 'focus:border-red-500 border-red-500': state.value === ERROR }
})
const isError = computed(() => {
	if (typeof state.value === 'boolean') {
		return !state.value
	}
	return state.value === ERROR
})
const iconPosition = computed(() => {
	return iconLeft.value && !iconRight.value ? LEFT : RIGHT
})
const isStandardTextInput = computed(() => {
	const standardInputs = [ TEXT, SEARCH, DATE, NUMBER, PASSWORD, EMAIL, TIME ]
	return standardInputs.includes(inputType.value)
})
const isTelephoneInput = computed(() => {
	return inputType.value === TEL
})
const displayIcon = computed(() => {
	if (isStandardTextInput.value || isTelephoneInput.value) {
		return showIcon.value
	}
	return false
})
const showErrorMessage = computed(() => {
	return isError.value && required.value && showErrorMessageOnInvalid.value && enableErrorMessage.value && !readOnly.value
})
const showInputInstruction = computed(() => {
	return !!inputInstruction?.value
})

const backgroundColor = computed(() => {
	if (props.lightBox) {
		return 'bg-white dark:bg-mx-green-800 shadow-inner'
	}
	return shadowBox.value ? 'bg-mx-gray-100 dark:bg-mx-green-800 shadow-inner' : bgColor.value ?? 'dark:bg-mx-green-800'
})

const borderClasses = computed(() => {
	if (shadowBox.value) {
		return 'border shadow-sm focus:border-mx-gray-300 dark:border-0 dark:focus:border-mx-gray-500'
	}
	if (readOnly.value) {
		return 'border-b-0'
	}
	return borderClass.value
})
const computedErrorMessage = computed(() => {
	return errorMessage.value || t('default-error-message')
})

function handleBlur(event: FocusEvent) {
	enableErrorMessage.value = true
	emit('blur', event)
}
function handleFocus(event: FocusEvent) {
	emit('focus', event)
}
function handleKeyup(event: KeyboardEvent) {
	emit('keyup', event)
}
</script>
